import React, { useState } from 'react';
import { Modal } from '../comps';
import styled from 'styled-components';
import { Poppins, Spacer } from 'src/common';
import Button from 'src/components/form/Button';
import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { Guard } from '../Guard';
import { Row } from './comps';
import { getPercentage } from 'src/utils/misc';
import { convertValuesToNumbers, parseInitialValues } from './util';
import _ from 'lodash';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { AssessmentQuery, useUpsertAssessmentMutation } from 'src/graphql/_generated';
import { useCache } from 'src/graphql';
import { useAuth } from 'src/state/auth';
import { showSuccessToast, toastMessages } from 'src/components/Toast';

const StyledModal = styled(Modal)`
  width: 960px;
  min-height: 200px;

  .divider {
    position: absolute;
    left: 0;
    width: 100%;
    border-bottom: 1px solid ${({ theme }) => theme.colors.stroke};
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .rtm-btns {
    display: grid;
    /* grid-template-columns: 1fr 1fr;
    grid-gap: 20px; */
  }

  .err-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    .rtm-btns {
      grid-gap: 10px;
    }
  }
`;

const validationSchema = Yup.array().of(
  Yup.object()
    .shape(
      {
        loss: Yup.number()
          .min(1, 'Minimum value is 1')
          .when(['frequency', 'years'], (values, schema) => {
            const hasValue = values.some((v) => v != undefined);
            return hasValue ? schema.required('Required') : schema.notRequired().nullable();
          }),

        frequency: Yup.number()
          .min(1, 'Minimum value is 1')
          .when(['loss', 'years'], (values, schema) => {
            const hasValue = values.some((v) => v != undefined);
            return hasValue ? schema.required('Required') : schema.notRequired().nullable();
          }),

        years: Yup.number()
          .min(1, 'Minimum value is 1')
          .when(['loss', 'frequency'], (values, schema) => {
            const hasValue = values.some((v) => v != undefined);
            return hasValue ? schema.required('Required') : schema.notRequired().nullable();
          }),
      },
      [
        ['loss', 'frequency'],
        ['loss', 'years'],
        ['frequency', 'years'],
      ],
    )
    .nullable(),
);

interface IRiskToleranceModalV2Props {
  assessment: NonNullable<AssessmentQuery['assessment']>;
  onClose: () => void;
  readOnly?: boolean;
}

export const RiskToleranceModalV2: React.FC<IRiskToleranceModalV2Props> = ({ onClose, assessment, readOnly }) => {
  const [isCloseRequested, setIsCloseRequested] = useState(false);
  const { refetchQuery } = useCache();
  const { workspaceId } = useAuth();

  const initialValues = parseInitialValues(assessment.riskTolerance);

  const [isSaving, setIsSaving] = useState(false);
  const [upsertAssessment, { error }] = useUpsertAssessmentMutation({
    onError: () => setIsSaving(false),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: async (val) => {
      setIsSaving(true);
      const mappedNew = val.map((el) => {
        const numericEl = convertValuesToNumbers(el);
        const validPercentageCalc = _.isNumber(numericEl.frequency) && _.isNumber(numericEl.years);

        if (!validPercentageCalc) return undefined;
        return {
          x: numericEl.loss,
          y: getPercentage(numericEl.frequency, numericEl.years) / 100,
          conf: numericEl,
        };
      });

      const filteredData = _.filter(mappedNew, (item) => {
        return (
          item && !_.some(item, (value) => _.isNil(value) || (_.isObject(value) && _.some(value, (v) => _.isNil(v))))
        );
      });

      const stringified = JSON.stringify(filteredData);

      await upsertAssessment({
        variables: { assessmentData: { id: assessment.id, riskTolerance: stringified }, workspaceId },
      });
      await refetchQuery({ include: ['assessment'] });
      showSuccessToast(toastMessages.riskTolerance.saved);
      onClose();
    },
  });

  const { values, isValid, dirty, handleSubmit } = formik;

  return (
    <Guard
      onCancel={() => setIsCloseRequested(false)}
      onClose={onClose}
      isOpen={isCloseRequested}
      isDirty={dirty}
      modalComponent={
        <StyledModal onRequestClose={() => setIsCloseRequested(true)} isOpen={true} persistent={isSaving}>
          <Poppins className="m-title" px={28}>
            Risk Tolerance
          </Poppins>
          <Spacer $px={10} />
          <Poppins px={16} color="cflowerBlue">
            How many times a year would this monetary loss be acceptable?
          </Poppins>
          <Spacer $px={40} />
          {/* @ts-ignore */}
          <FormikProvider value={formik}>
            {values.map((_, idx) => (
              <div key={idx}>
                <div className="divider"></div>
                <Row idx={idx} readOnly={readOnly} />
              </div>
            ))}
          </FormikProvider>
          <>
            {!readOnly && (
              <>
                <Spacer $px={20} />
                <div className="rtm-btns">
                  <Button
                    primary
                    disabled={isSaving || !dirty || !isValid}
                    loading={isSaving}
                    onClick={() => {
                      handleSubmit();
                      mpEvent(MPEvents.ButtonClick, {
                        button: 'Save',
                        modal: 'Risk tolerance modal',
                      });
                    }}
                    data-cy="submit-btn"
                  >
                    SAVE
                  </Button>
                </div>
              </>
            )}
            {!!error && (
              <div className="err-container">
                <Spacer $px={10} />
                <Poppins px={14} color="gp1">
                  {/* @ts-ignore */}
                  {error.message || 'Something went wrong'}
                </Poppins>
              </div>
            )}
          </>
        </StyledModal>
      }
    />
  );
};
